/**
 * Constants for the SuggestionWizard component
 */

import { ALL_INDUSTRIES, ALL_REGIONS, COMPANY_SIZES, CUSTOMER_TYPES, SELLING_MOTIONS } from './icpOptions';
import { PriorityConfig } from '../types/suggestionWizard';

// Subset of industries for wizard quick-select (first 10)
export const WIZARD_INDUSTRIES = ALL_INDUSTRIES.slice(0, 10);

// Subset of regions for wizard quick-select (first 10)
export const WIZARD_REGIONS = ALL_REGIONS.slice(0, 10);

// Company size values derived from existing COMPANY_SIZES
export const WIZARD_COMPANY_SIZES = COMPANY_SIZES.map(s => s.value);

// Priority configuration for confidence dots
export const PRIORITY_CONFIG: Record<string, PriorityConfig> = {
  high: { label: 'High', dots: 3, color: 'bg-emerald-500' },
  medium: { label: 'Medium', dots: 2, color: 'bg-amber-400' },
  low: { label: 'Low', dots: 1, color: 'bg-gray-300' },
};

/**
 * Get priority configuration by priority string
 */
export const getPriorityConfig = (priority: string): PriorityConfig => {
  const key = priority?.toLowerCase() || 'low';
  return PRIORITY_CONFIG[key] || PRIORITY_CONFIG.low;
};

// Wizard text constants
export const WIZARD_TEXT = {
  BRAND_NAME: 'Skylarq',
  BRAND_NAME_UPPER: 'SKYLARQ',
  SUBTITLE: 'Signal Generator',

  // Welcome messages
  WELCOME_WITH_ICPS: "Hey there! Ready to find some great leads?",
  WELCOME_ICP_SELECTION: "Pick one of your saved profiles below, or tell me about something new you're working on.",
  WELCOME_NO_ICPS: "Hey! I'm here to help you discover your perfect customers.",
  WELCOME_PROMPT: "Tell me a bit about what you're selling — the more context you share, the better signals I can find for you.",

  // Conversation flow messages
  PRODUCT_RESPONSE: "That sounds really interesting!",
  CUSTOMER_TYPE_QUESTION: "Who are you mainly selling to?",
  MOTION_QUESTION: "How do you usually close deals?",
  COMPANY_SIZE_QUESTION: "What size companies work best for you?",
  INDUSTRY_QUESTION: "Which industries do you focus on?",
  TITLES_QUESTION: "Who usually makes the buying decision?",
  REGIONS_QUESTION: "Any specific regions or countries?",

  // Loading/generating messages
  ICP_LOADING: (name: string) => `Great choice! Let me analyze "${name}" and find fresh opportunities...`,
  GENERATING: "On it! Let me find your best signals...",
  FINDING_SIGNALS: "Finding your best signals...",
  THINKING: "Thinking...",

  // Result messages
  SIGNALS_FOUND: (count: number) => `Here are ${count} signals I found. Pick the ones that look promising and I'll start finding leads.`,
  SIGNALS_TAILORED: (count: number) => `Here we go! I found ${count} signals tailored to your profile.`,
  SELECT_SIGNALS: "Select the ones you want to run — I've sorted them by relevance.",

  // Error messages
  ERROR_GENERIC: "Hmm, something went wrong on my end. Mind trying again?",

  // Empathetic responses by customer type
  CUSTOMER_TYPE_RESPONSES: {
    'B2B': "B2B — love it! There's so much opportunity in the business space.",
    'B2C': "B2C — exciting! Direct to consumer is where the magic happens.",
    'Both': "Best of both worlds! That gives us lots of angles to explore.",
  } as Record<string, string>,

  // Empathetic responses by selling motion
  MOTION_RESPONSES: {
    'sales-led': "Sales-driven approach — that's where strong relationships are built!",
    'plg': "Product-led growth — let the product do the talking!",
    'services': "Services model — nothing beats that personal touch!",
  } as Record<string, string>,

  // Skip action
  SKIP_GENERATE: "Skip questions, generate now →",

  // Button labels
  RUN_SIGNALS: (count: number) => `Run ${count} Signal${count > 1 ? 's' : ''}`,
  SELECT_SIGNALS_TO_RUN: "Select signals to run",
  USE_PROFILE: "Use this profile",
};

// Input placeholders by step
export const INPUT_PLACEHOLDERS: Record<string, string> = {
  generating: 'Generating signals...',
  ask_company_sizes: 'Select above or type sizes...',
  ask_industries: 'Select above or type industries...',
  ask_regions: 'Select above or type regions...',
  ask_titles: 'e.g., VP of Engineering, CTO...',
  ask_product: 'Describe what you sell...',
  default: 'Type a message...',
};

// Customer type options - extended from CUSTOMER_TYPES with wizard-specific descriptions
const CUSTOMER_TYPE_DESCRIPTIONS: Record<string, { label: string; description: string }> = {
  'B2B': { label: 'Businesses', description: 'Companies & organizations' },
  'B2C': { label: 'Consumers', description: 'Individual people' },
  'Both': { label: 'Both', description: 'B2B and B2C' },
};

export const CUSTOMER_TYPE_OPTIONS = CUSTOMER_TYPES.map(ct => ({
  value: ct.id,
  label: CUSTOMER_TYPE_DESCRIPTIONS[ct.id]?.label || ct.label,
  description: CUSTOMER_TYPE_DESCRIPTIONS[ct.id]?.description || '',
}));

// Selling motion options - extended from SELLING_MOTIONS with wizard-specific descriptions
const SELLING_MOTION_DESCRIPTIONS: Record<string, { label: string; description: string }> = {
  'sales-led': { label: 'Sales conversations', description: 'Demos, calls, meetings' },
  'plg': { label: 'Self-serve product', description: 'Users try before they buy' },
  'services': { label: 'Services & consulting', description: 'Custom solutions' },
};

export const SELLING_MOTION_OPTIONS = SELLING_MOTIONS.map(sm => ({
  value: sm.id,
  label: SELLING_MOTION_DESCRIPTIONS[sm.id]?.label || sm.label,
  description: SELLING_MOTION_DESCRIPTIONS[sm.id]?.description || '',
}));

// Panel dimensions
export const WIZARD_PANEL = {
  WIDTH: 400,
  ANIMATION_DURATION: 300,
};

// Carousel configuration
export const CAROUSEL_CONFIG = {
  CARD_WIDTH: 280,
  GAP: 12,
};

// Textarea configuration
export const TEXTAREA_CONFIG = {
  MIN_HEIGHT: 44,
  MAX_HEIGHT: 120,
};
